import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  Button,
  Icon,
  TextInput,
} from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import { useAudiences } from '../../../../models/audiences/context';
import './audience-picker-card.scss';
import { Audience } from '../../../../models/audiences/types';

const AudiencePickerCard = () => {
  const { t } = useTranslation();
  const { userGeneratedContent, updateUserGeneratedContentFormState } =
    useUserGeneratedContent();
  const { audiences } = useAudiences();
  const [dropdownSelection, setDropdownSelection] = useState<Audience[]>(
    userGeneratedContent.audiences ?? []
  );
  const [searchTerm, setSearchTerm] = useState('');

  const selectedAudiences = userGeneratedContent.audiences ?? [];
  const buttonLabel =
    selectedAudiences
      .map((a) => a.title)
      .sort()
      .join(', ') || t('user_generated_content.audience_picker_placeholder');

  const selectedNames = dropdownSelection.map((a) => a.name);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleCheckedChange = useCallback(
    (audience: Audience, checked: boolean) => {
      const updated = checked
        ? [...dropdownSelection, audience]
        : dropdownSelection.filter((a) => a.name !== audience.name);
      setDropdownSelection(updated);
    },
    [dropdownSelection]
  );

  const applyChanges = useCallback(() => {
    updateUserGeneratedContentFormState({ audiences: dropdownSelection });
    setIsDropdownOpen(false);
  }, [dropdownSelection, updateUserGeneratedContentFormState]);

  const filteredAudiences = useMemo(() => {
    if (!searchTerm) return audiences;
    return audiences.filter((a) =>
      a.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [audiences, searchTerm]);

  if (audiences.length === 0) {
    return null;
  }

  return (
    <div className="ugc-audience-picker-card">
      <div className="ugc-audience-picker-card__label">
        {t('user_generated_content.viewable_by')}
      </div>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            label={buttonLabel}
            rightIcon={<Icon>add</Icon>}
            size="compact"
            variant="outline"
            className={'ugc-audience-picker-card__trigger'}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="ugc-audience-picker-card__content"
        >
          <div className="ugc-audience-picker-card__search">
            <div className="ugc-audience-picker-card__cta">
              {t('user_generated_content.select_audiences')}
            </div>
            <TextInput
              className="ugc-audience-picker-card__search_input"
              fullWidth
              value={searchTerm}
              onChange={setSearchTerm}
              rightWidget={<Icon>search</Icon>}
              placeholder={t('user_generated_content.search_audiences')}
            />
            <div className="ugc-audience-picker-card__options">
              {filteredAudiences.length > 0 &&
                filteredAudiences.map((audience) => (
                  <DropdownMenuCheckboxItem
                    key={audience.name}
                    label={audience.title}
                    checked={selectedNames.includes(audience.name)}
                    onCheckedChange={(checked: boolean) =>
                      handleCheckedChange(audience, checked)
                    }
                    onSelect={(e: Event) => e.preventDefault()}
                    className={'ugc-audience-picker-card__option'}
                  />
                ))}
              {filteredAudiences.length === 0 && (
                <div>{t('user_generated_content.no_audiences_found')}</div>
              )}
            </div>
          </div>
          <div className="ugc-audience-picker-card__actions">
            <Button
              label={t('user_generated_content.clear_audience_selection')}
              variant="text"
              onClick={() => setDropdownSelection([])}
            />
            <Button
              label={t('user_generated_content.apply_audience_selection')}
              variant="primary"
              onClick={applyChanges}
            />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default AudiencePickerCard;
